import { logger } from '@fiverr-private/obs';
import { MIXPANEL_EVENT_PROPERTIES } from '../..';
import type { StoreWithSetter } from '../types';
import type { LogoStyle, LogoType } from './types';

export type Brief = Partial<{
    id: string;
    brandName: string;
    slogan: string;
    industries: string[];
    keywords: string[];
    brandValues: {
        classicOrModern: number;
        accessibleOrLuxurious: number;
        funOrSerious: number;
        simpleOrSophisticated: number;
        innovativeOrEstablished: number;
    };
    logoStyle: LogoStyle;
    logoType: LogoType;
}>;

type ProcessedBrief = ReturnType<typeof getBrief>;

type Options = {
    disableErrorLogging: boolean;
};

const {
    BRIEF_ID,
    BRAND_NAME,
    SLOGAN,
    INDUSTRY,
    INDUSTRY_COUNT,
    KEYWORDS,
    KEYWORDS_COUNT,
    CLASSIC_OR_MODERN,
    ACCESSIBLE_OR_LUXURIOUS,
    FUN_OR_SERIOUS,
    SIMPLE_OR_SOPHISTICATED,
    TRADITIONAL_OR_INNOVATIVE,
    LOGO_STYLE,
    LOGO_TYPE,
} = MIXPANEL_EVENT_PROPERTIES;

const getBrief = ({
    id,
    brandName,
    slogan,
    industries,
    keywords,
    brandValues,
    logoStyle,
    logoType,
}: Brief) =>
    ({
        ...(id && { [BRIEF_ID]: id }),
        ...(brandName !== undefined && { [BRAND_NAME]: brandName }),
        ...(slogan !== undefined && { [SLOGAN]: slogan }),
        ...(industries && {
            [INDUSTRY]: industries,
            [INDUSTRY_COUNT]: industries.length,
        }),
        ...(keywords && {
            [KEYWORDS]: keywords,
            [KEYWORDS_COUNT]: keywords.length,
        }),
        ...(brandValues && {
            [CLASSIC_OR_MODERN]: brandValues.classicOrModern,
            [ACCESSIBLE_OR_LUXURIOUS]: brandValues.accessibleOrLuxurious,
            [FUN_OR_SERIOUS]: brandValues.funOrSerious,
            [SIMPLE_OR_SOPHISTICATED]: brandValues.simpleOrSophisticated,
            [TRADITIONAL_OR_INNOVATIVE]: brandValues.innovativeOrEstablished,
        }),
        ...(logoStyle && { [LOGO_STYLE]: logoStyle }),
        ...(logoType && { [LOGO_TYPE]: logoType }),
    } as const);

export class BriefStore
    implements StoreWithSetter<Brief, ProcessedBrief | null>
{
    private _brief?: ProcessedBrief;

    private disableErrorLogging = false;

    private _rawBrief?: Brief;

    constructor(
        { disableErrorLogging = false }: Options = {
            disableErrorLogging: false,
        }
    ) {
        this.disableErrorLogging = disableErrorLogging;
    }

    public getRawData = () => this._rawBrief;

    public setData = (brief: Brief) => {
        this._rawBrief = brief;
        this._brief = getBrief(brief);
    };

    public getData = () => {
        if (!this._brief && !this.disableErrorLogging) {
            const error = new Error('No brief was set');
            logger.info(error);
        }

        return this._brief || null;
    };
}
